import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useDrop } from "react-dnd";
import {
  TrashIcon,
  VideoCameraIcon,
  FunnelIcon,
  PlusIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import PlaylistForm from "../../components/forms/PlaylistForm";
import { useAuth } from "../../hooks/useAuth";
import { usePlaylistAssignments, usePlaylists } from "../../hooks/usePlaylists";
import { useCreatives } from "../../hooks/useCreatives";
import {
  Playlist,
  PlaylistFilterFormData,
  PlaylistFilters,
  PlaylistFormData,
} from "../../schemas/playlistSchema";
import PlaylistFilterForm from "../../components/filters/PlaylistFilterModal";
import Loader from "../../components/common/Loader";
import PlaylistPreviewModal from "../../components/modals/PlaylistPreviewModal";
import Input from "../../components/common/Input";
import DraggableCreative from "../../components/subComponents/DraggableCreative";
import DraggablePlaylist from "../../components/subComponents/DraggablePlaylist";
import PlaylistCreativeList from "../../components/subComponents/PlaylistCreativeList";
import CustomDragLayer from "../../components/subComponents/CustomDragLayer";
import { ItemTypes, DEFAULT_PAGE_SIZE } from "../../constants";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";
import {
  getInsertIndexFromPointer,
  toStartPlayOrder,
} from "../../utils/playlistOrder";

const Playlists: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState<Playlist | null>(
    null,
  );
  const [formMode, setFormMode] = useState<"create" | "clone">("create");
  const [playlistFilters, setPlaylistFilters] = useState<PlaylistFilters>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [creativeFilters, setCreativeFilters] = useState<{
    page: number;
    pageSize: number;
    search?: string;
  }>({ page: 1, pageSize: 50 });
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [librarySearchValue, setLibrarySearchValue] = useState("");
  const librarySearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [allPlaylists, setAllPlaylists] = useState<Playlist[]>([]);
  const [hasMorePlaylists, setHasMorePlaylists] = useState(true);
  const playlistListRef = useRef<HTMLDivElement>(null);
  const [allCreatives, setAllCreatives] = useState<any[]>([]);
  const [hasMoreCreatives, setHasMoreCreatives] = useState(true);
  const libraryListRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreCreativesRef = useRef(false);
  const [selectedLibraryItems, setSelectedLibraryItems] = useState<any[]>([]);

  const {
    playlists,
    isPlaylistsLoading,
    createPlaylist,
    updatePlaylist,
    deletePlaylists,
    clonePlaylist,
    addCreativeToSubPlaylist,
    addSubPlaylistsToMainPlaylist,
    updateAssignmentCount,
    reorderPlaylistItems,
  } = usePlaylists(playlistFilters);

  const {
    playlistAssignments,
    isPlaylistAssignmentsLoading,
    refetchPlaylistAssignments,
    deletePlaylistItem,
  } = usePlaylistAssignments(selectedPlaylist?.id);

  const { creatives, isLoading: isCreativesLoading } =
    useCreatives(creativeFilters);
  const { canCreate, canDelete } = useAuthPermissions("playlist");

  const ref = useRef<HTMLDivElement>(null);
  const selectedPlaylistRef = useRef<Playlist | null>(null);
  // Live mirror of the library selection so the drag payload (built inside
  // react-dnd's item() closure) always sees the current selection.
  const selectedLibraryItemsRef = useRef<any[]>([]);

  const [isDropLoading, setIsDropLoading] = useState(false);

  // Position a dragged library item would be inserted at. State drives the drop
  // indicator, the ref is what the drop handler reads.
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  const insertIndexRef = useRef<number | null>(null);

  const updateInsertIndex = useCallback((next: number | null) => {
    if (insertIndexRef.current === next) return;
    insertIndexRef.current = next;
    setInsertIndex(next);
  }, []);

  useEffect(() => {
    selectedPlaylistRef.current = selectedPlaylist;
  }, [selectedPlaylist]);

  useEffect(() => {
    selectedLibraryItemsRef.current = selectedLibraryItems;
  }, [selectedLibraryItems]);

  // Build the drag payload: if a selected item is dragged, carry the whole
  // selection; otherwise carry just the dragged item.
  const getDragItem = useCallback((item: any) => {
    const selected = selectedLibraryItemsRef.current;
    const isSelected = selected.some((i) => i.id === item.id);
    return { items: isSelected ? selected : [item] };
  }, []);

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: [ItemTypes.CREATIVE, ItemTypes.PLAYLIST],
      hover: (_dragged: any, monitor) => {
        // Track the position under the pointer so the drop lands there instead
        // of at the bottom of the list.
        updateInsertIndex(
          getInsertIndexFromPointer(ref.current, monitor.getClientOffset()),
        );
      },
      drop: async (dragged: any, monitor) => {
        if (monitor.didDrop()) {
          return;
        }

        const playlist = selectedPlaylistRef.current;
        if (!playlist) {
          alert("Please select a playlist first");
          return;
        }

        // Drag payload always carries an `items` array (one item for a plain
        // drag, many when a selected item is dragged).
        const items = Array.isArray(dragged?.items)
          ? dragged.items
          : [dragged];
        if (items.length === 0) {
          return;
        }

        // Prefer the position the indicator was showing; hit-test again only if
        // the drop somehow arrived without a preceding hover.
        const targetIndex =
          insertIndexRef.current ??
          getInsertIndexFromPointer(ref.current, monitor.getClientOffset());
        updateInsertIndex(null);
        setIsDropLoading(true);

        try {
          // The backend inserts at this 1-based slot and shifts the rest down;
          // undefined appends.
          const startPlayOrder = toStartPlayOrder(targetIndex);

          // The library only ever shows items valid for the selected playlist
          // (creatives for a sub playlist, sub playlists for a main playlist),
          // so the target type decides how each item is added.
          if (playlist.type === "sub") {
            await addCreativeToSubPlaylist({
              subPlaylistId: playlist.id!,
              creativeIds: items.map((item: any) => item.id),
              startPlayOrder,
            });
          } else if (playlist.type === "main") {
            await addSubPlaylistsToMainPlaylist({
              mainPlaylistId: playlist.id!,
              subPlaylistIds: items.map((item: any) => item.id),
              startPlayOrder,
            });
          }

          await refetchPlaylistAssignments();
          setSelectedLibraryItems([]);
        } catch (error) {
          alert("Failed to add items to playlist");
        } finally {
          // Stop loader
          setIsDropLoading(false);
        }
      },
      collect: (monitor) => {
        try {
          return {
            isOver: monitor.isOver({ shallow: true }),
          };
        } catch (error) {
          // Handle case where monitor loses track of target
          return { isOver: false };
        }
      },
    }),
    [
      refetchPlaylistAssignments,
      addCreativeToSubPlaylist,
      addSubPlaylistsToMainPlaylist,
      updateInsertIndex,
    ],
  );

  // Clear the indicator as soon as the drag leaves the drop area.
  useEffect(() => {
    if (!isOver) {
      updateInsertIndex(null);
    }
  }, [isOver, updateInsertIndex]);

  // Use useCallback to ensure drop ref is stable
  const dropRef = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node;
      drop(node);
    },
    [drop],
  );

  const handleSelect = (playlist: Playlist) => {
    setSelectedPlaylist(playlist);
    refetchPlaylistAssignments();
  };

  const handleDelete = (playlist: Playlist) => {
    if (window.confirm(`Delete playlist "${playlist.name}"?`)) {
      deletePlaylists([playlist.id!]);
    }
  };

  const handleSubmit = async (data: PlaylistFormData | any) => {
    try {
      if (formMode === "clone") {
        await clonePlaylist({ id: selectedPlaylist?.id!, name: data.name });
      } else {
        await createPlaylist(data);
      }
      setIsModalOpen(false);
      setSelectedPlaylist(null);
      setFormMode("create");
    } catch (error) {
      alert("Failed to save playlist");
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedPlaylist(null);
    setFormMode("create");
  };

  const handleApplyFilter = async (data: PlaylistFilterFormData) => {
    setPlaylistFilters((prev) => ({
      ...prev,
      ...data,
      tagIds: data.tag ? [data.tag] : undefined,
      page: 1,
    }));
    setIsFilterModalOpen(false);
  };

  const handleResetFilter = () => {
    setPlaylistFilters({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
    setIsFilterModalOpen(false);
  };

  // Clear filter-related values but keep modal open (used by Reset in modal)
  const handleClearFilterValues = () => {
    setPlaylistFilters((prev) => ({
      page: 1,
      pageSize: prev.pageSize || 10,
      // Preserve search if present
      ...(prev.search ? { search: prev.search } : {}),
    }));
  };

  const handleCloseFilter = () => {
    setIsFilterModalOpen(false);
  };

  // Check if any filters are applied (excluding page, pageSize)
  const hasActiveFilters = useMemo(() => {
    return !!(
      playlistFilters.type ||
      playlistFilters.tag ||
      (playlistFilters.tagIds && playlistFilters.tagIds.length > 0)
    );
  }, [playlistFilters]);

  const handleDeleteCreative = async (creativeId: string) => {
    if (!selectedPlaylist) return;
    if (!window.confirm("Remove this item from playlist?")) return;

    try {
      await deletePlaylistItem(creativeId);
      await refetchPlaylistAssignments();
    } catch (error) {
      alert("Failed to remove creative from playlist");
    }
  };

  const handleUpdateCount = async (assignmentId: string, count: number) => {
    if (!selectedPlaylist || count < 1) return;

    try {
      await updateAssignmentCount({
        assignmentId,
        count,
      });

      // Refresh assignments to ensure UI reflects new repetitionCount
      await refetchPlaylistAssignments();
    } catch (error) {
      alert("Failed to update creative count");
    }
  };

  const handleReorder = async (assignmentIds: string[]) => {
    if (!selectedPlaylist) return;

    try {
      await reorderPlaylistItems({
        playlistId: selectedPlaylist.id!,
        assignmentIds,
      });

      // Refresh assignments to ensure UI reflects new order
      await refetchPlaylistAssignments();
    } catch (error) {
      alert("Failed to reorder playlist items");
    }
  };

  const handleCopy = async (playlist: Playlist) => {
    setSelectedPlaylist({ ...playlist, tagIds: playlist?.tagIds || [] });
    setFormMode("clone");
    setIsModalOpen(true);
  };

  useEffect(() => {
    const playlistData = playlists?.playlists || [];
    const playlistTotal = playlists?.total || 0;
    if (playlistFilters.page === 1) {
      setAllPlaylists(playlistData);
      setHasMorePlaylists(playlistTotal > playlistData.length);
    } else if (
      playlistData.length &&
      playlistFilters.page &&
      playlistFilters.page > 1
    ) {
      setAllPlaylists((prev) => [...prev, ...playlistData]);
      setHasMorePlaylists(
        playlistTotal > allPlaylists.length + playlistData.length,
      );
    }
  }, [playlists]);

  useEffect(() => {
    const creativeData = creatives?.creatives || [];
    const creativeTotal = creatives?.total || 0;
    if (creativeFilters.page === 1) {
      setAllCreatives(creativeData);
      setHasMoreCreatives(creativeTotal > creativeData.length);
      isLoadingMoreCreativesRef.current = false;
    } else if (creativeData.length && creativeFilters.page > 1) {
      setAllCreatives((prev) => [...prev, ...creativeData]);
      setHasMoreCreatives(
        creativeTotal > allCreatives.length + creativeData.length,
      );
      isLoadingMoreCreativesRef.current = false;
    }
  }, [creatives]);

  useEffect(() => {
    const handleScroll = () => {
      const el = playlistListRef.current;
      if (!el || isPlaylistsLoading || !hasMorePlaylists) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
        setPlaylistFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }));
      }
    };
    const el = playlistListRef.current;
    if (el) {
      el.addEventListener("scroll", handleScroll);
      return () => el.removeEventListener("scroll", handleScroll);
    }
  }, [isPlaylistsLoading, hasMorePlaylists]);

  useEffect(() => {
    const handleScroll = () => {
      const el = libraryListRef.current;
      if (!el || isLoadingMoreCreativesRef.current || !hasMoreCreatives) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
        isLoadingMoreCreativesRef.current = true;
        setCreativeFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }));
      }
    };
    const el = libraryListRef.current;
    if (el) {
      el.addEventListener("scroll", handleScroll);
      return () => el.removeEventListener("scroll", handleScroll);
    }
  }, [hasMoreCreatives]);

  // Helper to check if an item is selected
  const isLibraryItemSelected = (item: any) =>
    selectedLibraryItems.some((i) => i.id === item.id);

  // Handler to toggle selection
  const handleLibraryItemSelect = (item: any) => {
    setSelectedLibraryItems((prev) => {
      if (isLibraryItemSelected(item)) {
        return prev.filter((i) => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  // Handler to add selected items to playlist. A click carries no drop
  // position, so these always append (the backend picks the next play order).
  const handleAddSelectedToPlaylist = async () => {
    if (!selectedPlaylist || selectedLibraryItems.length === 0) return;
    try {
      if (selectedPlaylist.type === "sub") {
        await addCreativeToSubPlaylist({
          subPlaylistId: selectedPlaylist.id!,
          creativeIds: selectedLibraryItems.map((item) => item.id),
        });
      } else if (selectedPlaylist.type === "main") {
        await addSubPlaylistsToMainPlaylist({
          mainPlaylistId: selectedPlaylist.id!,
          subPlaylistIds: selectedLibraryItems.map((item) => item.id),
        });
      }

      // Refresh assignments
      await refetchPlaylistAssignments();
      setSelectedLibraryItems([]);
    } catch (error) {
      alert("Failed to add items to playlist");
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-auto md:h-[90vh] gap-2 md:gap-0 p-4">
      <CustomDragLayer />
      <div className="w-full md:w-1/4 md:pr-4 border-b md:border-b-0 md:border-r border-primary-default flex flex-col md:h-full">
        <div className="flex justify-between mb-4">
          <h1 className="text-2xl font-bold">Playlists</h1>
          <span>
            <button
              className="relative p-2 mr-2 bg-primary-default text-white rounded hover:bg-primary-light"
              onClick={() => setIsFilterModalOpen(true)}
            >
              <FunnelIcon className="h-5 w-5" />
              {hasActiveFilters && (
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"></span>
              )}
            </button>
            {canCreate && (
              <button
                className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
                onClick={() => {
                  setSelectedPlaylist(null);
                  setFormMode("create");
                  setIsModalOpen(true);
                }}
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            )}
          </span>
        </div>
        <Input
          placeholder="Search playlists..."
          value={searchValue}
          onChange={(e) => {
            const value = e.target.value;
            setSearchValue(value);
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
            searchTimeout.current = setTimeout(() => {
              setPlaylistFilters((prev) => ({
                ...prev,
                search: value || undefined,
                page: 1,
              }));
            }, 300);
          }}
          icon={<VideoCameraIcon className="h-5 w-5 text-gray-400" />}
        />
        <div className="flex-1 md:overflow-y-auto" ref={playlistListRef}>
          {(isPlaylistsLoading || isCreativesLoading) &&
          allPlaylists.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <Loader />
            </div>
          ) : allPlaylists.length > 0 ? (
            allPlaylists.map((playlist: Playlist) => (
              <div
                key={playlist.id}
                className={`flex items-center gap-2 px-2 py-2 cursor-pointer rounded-lg mb-1 ${
                  selectedPlaylist?.id === playlist.id
                    ? "border border-primary-light"
                    : "hover:bg-gray-200"
                }`}
                onClick={() => handleSelect(playlist)}
              >
                <VideoCameraIcon className="h-6 w-6 text-primary-default" />
                <span className="truncate flex-1">{playlist.name}</span>
                {canCreate && (
                  <button
                    className="p-1 hover:bg-gray-200 rounded"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy(playlist);
                    }}
                    title="Copy Playlist"
                  >
                    <DocumentDuplicateIcon className="h-5 w-5 text-primary-default" />
                  </button>
                )}
                {canDelete && (
                  <button
                    className="p-1 hover:bg-gray-200 rounded"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(playlist);
                    }}
                    title="Delete Playlist"
                  >
                    <TrashIcon className="h-5 w-5 text-danger-default" />
                  </button>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg font-medium mb-2">
                No data found
              </div>
              <div className="text-gray-400 text-sm">
                There are no items to display at the moment.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="w-full md:w-1/2 p-2 md:p-4 border-b md:border-b-0 md:border-r border-primary-default flex flex-col md:h-full">
        <div className="flex items-center justify-between gap-4 mb-4">
          <h2 className="text-xl whitespace-nowrap">
            {selectedPlaylist?.type === "main" ? "Sub Playlists" : "Library"}
          </h2>
          {selectedPlaylist && selectedPlaylist.type !== "main" && (
            <div className="w-1/2">
              <Input
                placeholder="Search library..."
                value={librarySearchValue}
                onChange={(e) => {
                  const value = e.target.value;
                  setLibrarySearchValue(value);
                  if (librarySearchTimeout.current)
                    clearTimeout(librarySearchTimeout.current);
                  librarySearchTimeout.current = setTimeout(() => {
                    setCreativeFilters((prev) => ({
                      ...prev,
                      search: value || undefined,
                      page: 1,
                    }));
                  }, 300);
                }}
                icon={<MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />}
              />
            </div>
          )}
        </div>
        <div className="flex-1 overflow-auto min-h-0" ref={libraryListRef}>
          {!selectedPlaylist ? (
            <div className="text-center py-8 text-gray-500">
              Select a playlist to view the library
            </div>
          ) : selectedPlaylist.type === "main" ? (
            isPlaylistsLoading ? (
              <div className="flex justify-center items-center h-full">
                <Loader />
              </div>
            ) : (
              (() => {
                const subPlaylists =
                  playlists?.playlists?.filter(
                    (playlist: Playlist) => playlist.type === "sub",
                  ) || [];
                return subPlaylists.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {subPlaylists.map((playlist: Playlist) => (
                      <div
                        key={playlist.id}
                        className={`relative group border-2 rounded-lg ${
                          isLibraryItemSelected(playlist)
                            ? "border-blue-500"
                            : "border-transparent"
                        }`}
                        onClick={() => handleLibraryItemSelect(playlist)}
                        style={{ cursor: "pointer" }}
                      >
                        <DraggablePlaylist
                          playlist={playlist}
                          getDragItem={getDragItem}
                        />
                        {isLibraryItemSelected(playlist) && (
                          <span className="absolute top-2 right-2 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                            ✓
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-gray-500 text-lg font-medium mb-2">
                      No data found
                    </div>
                    <div className="text-gray-400 text-sm">
                      There are no items to display at the moment.
                    </div>
                  </div>
                );
              })()
            )
          ) : isCreativesLoading ? (
            <div className="flex justify-center items-center h-full">
              <Loader />
            </div>
          ) : allCreatives.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {allCreatives.map((creative) => (
                <div
                  key={creative.id}
                  className={`relative group border-2 rounded-lg ${
                    isLibraryItemSelected(creative)
                      ? "border-blue-500"
                      : "border-transparent"
                  }`}
                  onClick={() => handleLibraryItemSelect(creative)}
                  style={{ cursor: "pointer" }}
                >
                  <DraggableCreative
                    creative={creative}
                    getDragItem={getDragItem}
                  />
                  {isLibraryItemSelected(creative) && (
                    <span className="absolute top-2 right-2 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                      ✓
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg font-medium mb-2">
                No data found
              </div>
              <div className="text-gray-400 text-sm">
                There are no items to display at the moment.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="w-full md:w-1/4 md:pl-4 flex flex-col md:h-full">
        <div className="flex justify-between mb-4">
          <h2 className="text-xl">
            {selectedPlaylist?.name || "Select a Playlist"}
          </h2>
          <div className="flex gap-2">
            {selectedPlaylist && selectedLibraryItems.length > 0 && (
              <button
                className="px-3 py-1 bg-success-default text-white rounded hover:bg-green-700"
                onClick={handleAddSelectedToPlaylist}
              >
                Add
              </button>
            )}
            {selectedPlaylist && (
              <button
                className="px-3 py-1 bg-primary-default text-white rounded hover:bg-primary-light"
                onClick={() => setIsPreviewOpen(true)}
              >
                Preview
              </button>
            )}
          </div>
        </div>
        <div
          ref={dropRef}
          className={`flex-1 border-2 border-dashed rounded-lg bg-white p-2 md:p-4 transition-colors ${
            isOver ? "border-primary-light bg-blue-50" : "border-gray-300"
          } md:min-h-[120px] overflow-auto`}
        >
          {isPlaylistAssignmentsLoading || isDropLoading ? (
            <div className="flex flex-col gap-2 animate-pulse">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          ) : selectedPlaylist ? (
            <PlaylistCreativeList
              creatives={playlistAssignments || []}
              onDelete={handleDeleteCreative}
              onUpdateCount={handleUpdateCount}
              onReorder={handleReorder}
              selectedPlaylist={selectedPlaylist}
              scrollContainerRef={ref}
              insertIndex={insertIndex}
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              Select a playlist to view its creatives
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <PlaylistForm
          visible={isModalOpen}
          initialData={
            selectedPlaylist
              ? {
                  name: selectedPlaylist.name,
                  type: selectedPlaylist.type as "main" | "sub",
                  isActive: selectedPlaylist.isActive,
                  tagIds: selectedPlaylist.tagIds,
                }
              : undefined
          }
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          mode={formMode}
        />
      )}

      {isFilterModalOpen && (
        <PlaylistFilterForm
          visible={isFilterModalOpen}
          initialData={{
            type: playlistFilters.type as "main" | "sub" | undefined,
            tag: playlistFilters.tag || playlistFilters.tagIds?.[0],
          }}
          onSubmit={handleApplyFilter}
          onCancel={handleResetFilter}
          onClose={handleCloseFilter}
          onReset={handleClearFilterValues}
        />
      )}

      {isPreviewOpen && (
        <PlaylistPreviewModal
          isOpen={isPreviewOpen}
          playlist={selectedPlaylist}
          assignments={playlistAssignments || []}
          onClose={() => setIsPreviewOpen(false)}
          onDeleteCreative={handleDeleteCreative}
          onReorder={handleReorder}
        />
      )}
    </div>
  );
};

export default Playlists;
